import { Component, ElementRef, AfterViewInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, Column } from 'ag-grid-community';
import { BtnCellClickRendererComponent } from '../btn-cell-click-renderer.component';
import { BtnIconCellRendererComponent } from '../btn-icon-cell-renderer.component';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
	selector: 'app-mobile-renderer',
	templateUrl: './mobile-renderer.component.html',
	styleUrls: ['./mobile-renderer.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MobileRendererComponent implements ICellRendererAngularComp, AfterViewInit, OnDestroy {
	params: any;
	data: any;
	columns: Column[];
	actions: any[];
	isSelectionEnabled: boolean = false;
	private resizeObserver: ResizeObserver | null = null;

	constructor(
		private _router: Router,
		private _route: ActivatedRoute,
		private elementRef: ElementRef
	) {}

	ngAfterViewInit() {
		this.resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				if (entry.contentRect.height > 0 && this.params && this.params.node) {
					// Add buffer for margins (e.g. 20px bottom margin)
					const newHeight = entry.contentRect.height + 30;
					if (this.params.node.rowHeight !== newHeight) {
						this.params.node.setRowHeight(newHeight);
						this.params.api.onRowHeightChanged();
					}
				}
			}
		});
		// Observe the first child (the card) if possible, otherwise the host
		const cardElement = this.elementRef.nativeElement.querySelector('.mobile-card');
		if (cardElement) {
			this.resizeObserver.observe(cardElement);
		} else {
			this.resizeObserver.observe(this.elementRef.nativeElement);
		}
	}

	ngOnDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}

	agInit(params: ICellRendererParams): void {
		this.params = params;
		this.data = params.data;
		const componentParent = (params.context as any).componentParent;
		this.columns = params.api.getColumns();
		this.actions = componentParent?.actions || [];
		const isRowSelectable = componentParent?.isRowSelectable;
		this.isSelectionEnabled = !!componentParent?.rowSelection && (!isRowSelectable || isRowSelectable(params.node));
	}

	isSelected(): boolean {
		return this.params.node.isSelected();
	}

	toggleSelection(event: any) {
		this.params.node.setSelected(event.target.checked);
	}

	refresh(params: ICellRendererParams): boolean {
		this.params = params;
		this.data = params.data;
		return true;
	}

	getValue(col: Column): any {
		return this.params.api.getCellValue({
			colKey: col,
			rowNode: this.params.node,
			useFormatter: true,
		});
	}

	isVisible(col: Column): boolean {
		return col.isVisible();
	}

	isBtnRenderer(col: Column): boolean {
		return col.getColDef().cellRenderer === BtnCellClickRendererComponent;
	}

	onBtnClick(col: Column) {
		const params = col.getColDef().cellRendererParams;
		if (params.onClick && typeof params.onClick === 'function') {
			params.onClick(this.data);
		} else if (params.url && this.data) {
			const url = params.url.replace(/\{(\w+)\}/g, (_, key) => this.data[key] ?? '');
			this._router.navigate([url], { relativeTo: this._route });
		}
	}

	getBtnIcon(col: Column) {
		const params = col.getColDef().cellRendererParams || {};
		return params.icon || 'pen-to-square';
	}

	getBtnClass(col: Column) {
		const params = col.getColDef().cellRendererParams || {};
		if (params.class) {
			return params.class;
		} else if (params.label || params.value) {
			return 'btn btn-primary stile-btn';
		} else {
			return 'btn btn-primary btn-icon';
		}
	}

	getBtnText(col: Column) {
		const params = col.getColDef().cellRendererParams || {};
		if (params.label) return params.label;
		if (params.value) return params.value;
		return '';
	}

	// BtnIconCellRenderer Support
	isBtnIconRenderer(col: Column): boolean {
		const cellRenderer = col.getColDef().cellRenderer;
		return cellRenderer && (cellRenderer.name === 'BtnIconCellRendererComponent' || cellRenderer === BtnIconCellRendererComponent);
	}

	isStatusRenderer(col: Column): boolean {
		const cellRenderer = col.getColDef().cellRenderer;
		// Check by name or reference, 'StatusCellRendererComponent'
		return (
			cellRenderer &&
			(cellRenderer.name === 'StatusCellRendererComponent' || cellRenderer.toString().includes('StatusCellRendererComponent'))
		);
	}

	getBtnIconClass(col: Column) {
		const params = col.getColDef().cellRendererParams || {};
		return params.class || 'btn btn-primary btn-icon';
	}

	getBtnIconIcon(col: Column) {
		// Default icon for BtnIconCellRenderer is 'pen-to-square' if not specified
		const params = col.getColDef().cellRendererParams || {};
		return params.icon || 'pen-to-square';
	}

	onBtnIconClick(col: Column) {
		const params = col.getColDef().cellRendererParams || {};
		let url = params.url;

		if (!url) return;

		// Replicate URL parsing logic from BtnIconCellRendererComponent
		let indexStart = 0;
		const paramsName = [];
		let tempUrl = url;
		while (1) {
			indexStart = tempUrl.indexOf('{') + 1;

			if (indexStart === 0) {
				break;
			}

			let paramTemp = tempUrl.substr(indexStart);
			tempUrl = paramTemp;

			paramTemp = paramTemp.substr(0, paramTemp.indexOf('}'));
			paramsName.push(paramTemp);
		}

		if (url == '/') {
			// Specific logic from original component
			url = `${this.data.cohort_school_university_of_x_school_university?.id}/edit`;
		} else {
			for (let paramName of paramsName) {
				const originalParamName = paramName;
				paramName = paramName.split('.');

				let paramValue = undefined;

				let first = true;
				for (const paramNamePath of paramName) {
					if (first) {
						paramValue = this.data[paramNamePath];
					} else {
						paramValue = paramValue[paramNamePath];
					}
					first = false;
				}

				url = url.replace(`{${originalParamName}}`, this.data[originalParamName] || paramValue);
			}
		}

		if (url.startsWith('/')) {
			this._router.navigate([url]);
		} else {
			this._router.navigate([url], { relativeTo: this._route });
		}
	}
}
